type OnStartType = (
  status: number,
  statusText: string,
  contentType: string | null,
  headers: Headers
) => void

type OnProgressType = (chunk: string) => void

type OnFinishType = (error: Error | undefined) => void

// Drop-in for event-source-polyfill's own FetchTransport (passed as its
// `Transport` option), with one difference. Once the stream has failed
// mid-body ("TypeError: network error" in Chrome), the reader is left errored
// and `reader.cancel()` rejects with that same error. The polyfill cancels it
// on every close() without a catch, so each dropped stream that gets closed
// also raised an unhandled rejection. That error has already reached onFinish,
// so the cancel rejection carries nothing new and is dropped.
class SseFetchTransport {
  open(
    _xhr: unknown,
    onStart: OnStartType,
    onProgress: OnProgressType,
    onFinish: OnFinishType,
    url: string,
    withCredentials: boolean,
    headers: Record<string, string>
  ) {
    const controller = new AbortController()
    const textDecoder = new TextDecoder()
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null

    async function readStream(): Promise<Error | undefined> {
      try {
        const response = await fetch(url, {
          headers,
          credentials: withCredentials ? 'include' : 'same-origin',
          signal: controller.signal,
          cache: 'no-store',
        })
        if (!response.body) throw new TypeError('Stream response has no body')

        reader = response.body.getReader()
        onStart(
          response.status,
          response.statusText,
          response.headers.get('Content-Type'),
          response.headers
        )

        for (;;) {
          const { done, value } = await reader.read()
          if (done) return undefined
          onProgress(textDecoder.decode(value, { stream: true }))
        }
      } catch (error) {
        const streamError = error as Error
        return streamError.name === 'AbortError' ? undefined : streamError
      }
    }

    void readStream().then(onFinish)

    return {
      abort() {
        // Already reported through onFinish; see the note above
        reader?.cancel().catch(() => undefined)
        controller.abort()
      },
    }
  }
}

export { SseFetchTransport }
